import React, { useEffect, useState, useRef, useMemo, lazy, Suspense } from "react";
import "../style/ticketInbox.css";
import {
	Tooltip,
	Button,
	Dropdown,
	Notification,
	toast,
	Tabs,
	Skeleton,
} from "components/ui";
import {
	HiOutlineStar,
	HiStar,
	HiFlag,
	HiOutlineFlag,
	HiOutlineDuplicate,
	HiOutlineTrash,
	HiOutlineDotsVertical,
	HiOutlineLink,
	HiOutlinePlus,
} from "react-icons/hi";
import {
	apiGetTicketsCategoriesById,
} from "services/TicketCategoryTypeServices";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useThemeClass from "utils/hooks/useThemeClass";
import ReactHtmlParser from "react-html-parser";
import { convertUrlToLink } from "utils/storeHube/helpers";
import TabNav from "components/ui/Tabs/TabNav";
import TabList from "components/ui/Tabs/TabList";
import TabContent from "components/ui/Tabs/TabContent";
import TicketRecod from "./TicketRecord";
import TicketActivity from "./TicketActivity";
import TicketAddComment from "./TicketAddComment";
import TicketLinkedCard from "./TicketLinkedCard";
import TicketTopbar from "./TicketTopbar";
import TicketTitleContext from "./TicketTitleContext";
import TicketMetaStrip from "./TicketMetaStrip";
import TicketDetailsCard from "./TicketDetailsCard";
import TicketAttachmentsCard from "./TicketAttachmentsCard";
import TicketAttachmentGallery from "./TicketAttachmentGallery";
import TicketPrimaryActions from "./TicketPrimaryActions";
import { CtaGroup, deriveCtaContext } from "./TicketHeaderActions";
import TicketDeleteConfirmation from "../ticketList/TicketDeleteConfirmation";
import TicketChangeStatusConfitmation from "../ticketList/TicketChangeStatusConfitmation";
import ChangeOperatorDialog from "./ChangeOperatorDialog";
import { LinkTicketDialog } from "./LinkTicketButton";
import { timeDiff } from "utils/storeHube/helpersDate";
import useResponsive from "utils/hooks/useResponsive";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import es from "dayjs/locale/es";

// Solo el cuestionario queda lazy: no todos los tickets lo tienen y su chunk es
// el que justifica el split. Activity y AddComment se usan siempre, lazy las
// hacia parpadear con un fallback ajeno (skeleton de cuestionario).
const TicketAnswers = lazy(() => import("./TicketAnswers"));
const TicketQuestion = lazy(() => import("./TicketQuestion"));

const AnswersSkeleton = () => (
	<div className="mt-2 px-1">
		<div className="flex items-center justify-between gap-3 mb-4">
			<Skeleton height={18} width={200} className="!rounded-md" />
			<div className="flex gap-2">
				<Skeleton height={20} width={70} className="!rounded-full" />
				<Skeleton height={20} width={70} className="!rounded-full" />
			</div>
		</div>
		{/* border-t por item en vez de divide-y: las clases divide-* a veces
			no las levanta el watcher JIT al startup, igual que en
			QuestionnaireWrapper. border-t es base, siempre presente. */}
		<div>
			{[0, 1, 2].map((i) => (
				<div
					key={i}
					className={i === 0
						? 'py-3 space-y-1.5'
						: 'py-3 space-y-1.5 border-t border-gray-100 dark:border-gray-700/60'
					}
				>
					<Skeleton height={12} width="60%" className="!rounded" />
					<Skeleton height={12} width="35%" className="!rounded" />
				</div>
			))}
		</div>
	</div>
);

const QuestionnaireSkeleton = () => (
	<div className="mt-6 px-1">
		<div className="mb-5">
			<Skeleton height={12} width={140} className="mb-2" />
			<Skeleton height={40} width="100%" className="!rounded-md" />
		</div>
		<div className="mb-5">
			<Skeleton height={12} width={180} className="mb-2" />
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-2">
					<Skeleton variant="circle" height={16} width={16} />
					<Skeleton height={12} width={60} />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton variant="circle" height={16} width={16} />
					<Skeleton height={12} width={60} />
				</div>
			</div>
		</div>
		<div className="mb-6">
			<Skeleton height={12} width={200} className="mb-2" />
			<Skeleton height={88} width="100%" className="!rounded-md" />
		</div>
		<Skeleton height={40} width="100%" className="!rounded-md" />
	</div>
);

dayjs.extend(relativeTime);
dayjs.locale(es);

// Helper puro: un cuestionario "solo titulo" tiene una sola pregunta de tipo
// titulo (question_type_id === 8). Importado desde TicketContent via export nombrado.
// Duplicamos aqui para no crear dependencia circular (TicketContent -> TicketDetail).
export const isQuestionnaireOnlyTitle = (questionGroup) =>
	questionGroup?.questions?.length === 1 &&
	questionGroup?.questions[0]?.question_type_id === 8;

const NEW_TICKET = 1;
const ASSIGNED = 2;
const CANCELLED = 3;
const CLOSED = 4;
const DELETED = 5;
const SYS_CLOSED = 6;
const REOPEN = 7;

/**
 * TicketDetail — componente presentacional puro.
 *
 * Recibe toda la data derivada, permisos y callbacks via props (el "vm"
 * generado por useTicketDetail). No contiene useSelector ni useDispatch.
 *
 * Estado local permitido: UI state del cuestionario/tabs (selectedTab,
 * questionGroup, etc.) y wrappers de loading minimos (loadingFavorite,
 * loadingStatus).
 *
 * Los sub-componentes que se renderizan (ChangeOperatorDialog,
 * TicketDeleteConfirmation, LinkTicketDialog) usan Redux internamente;
 * eso es aceptable porque son modales autonomos con su propio acceso al store.
 */
export default function TicketDetail({
	data,
	inDialog = false,
	// vm props
	ticket,
	currentUser,
	ticketStatus,
	allUsers,
	responsableMap,
	loading,
	loadingUsersAllowed,
	isAssignedToMe,
	canViewQuestionAndResponse,
	questionnaireState: questionnaireStateProp,
	ticketOrigin,
	isInformational,
	fromMentionsView = false,
	permissions,
	onChangeStatus,
	onChangeOperator,
	onMarkAsRead,
	onToggleFavorite,
	onQuestionnaireResolution,
	onMarkInProgress,
	onOpenParent,
	onOpenTicket,
	onDelete,
	onAddLinkedTicket,
	onSetSubscriber,
	onLinkExisting,
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { textTheme } = useThemeClass();
	const { smaller } = useResponsive();

	// Desestructuramos de `data` igual que TicketContent, ya que `data` es el
	// ticket a renderizar (coincide con `ticket` del vm en inbox).
	const {
		id,
		description,
		is_favorite,
		created_at,
		expired_at,
		sample_since,
		sample_until,
		second_start,
		props,
		parent_id,
		external_id,
		external_code,
		audit_cam,
	} = data;

	const {
		owner,
		store,
		operator,
		ticket_subscriptions,
		ticket_reply,
		ticket_log,
		ticket_category_primary,
		ticket_status,
		ticket_attachment,
		ticket_answer,
		record,
		ticket_category_secondary,
		ticket_answer_attempt,
		ticket_evidence,
	} = props;

	// Permisos desde vm.permissions (sin checkPermission / usePermissions)
	const {
		canUpdate: tickets_can_update,
		canChangeStatus: tickets_can_change_status,
		canClose: tickets_can_close,
		canReopen: tickets_can_reopen,
		canCancel: tickets_can_cancel,
		canLink: tickets_can_link,
		canViewTimeline: tickets_can_view_timeline,
	} = permissions || {};

	const { pathname, search } = useLocation();

	// Se entra por una mencion cuando la campanita pide un comentario puntual
	// (`?reply=`) o cuando la lista activa es la vista "Menciones": ahi cada
	// hallazgo esta justamente porque alguien nombro a esta persona. En los dos
	// casos lo que se viene a leer es el comentario, no el cuestionario.
	//
	// Si no hay permiso de timeline la pestania de actividad no existe (ver el
	// TabNav, que la condiciona), asi que seleccionarla dejaria el detalle sin
	// ninguna pestania activa.
	const anclaComentario = new URLSearchParams(search || "").get("reply");
	const vengoPorUnaMencion =
		(!!anclaComentario || fromMentionsView) && !!tickets_can_view_timeline;

	// --- Estado local de UI ---
	// Tab default: si es el asignado y tiene cuestionario, abre en "questions".
	//
	// La mencion se resuelve aca y no solo en un efecto: los efectos corren
	// despues del primer paint, asi que arrancar en "questions" para saltar a
	// "activity" un frame despues se ve como un parpadeo de pestania.
	const [selectedTab, setSelectedTab] = useState(() => {
		if (vengoPorUnaMencion) return "activity";
		const hasQuestionary =
			!!ticket_category_secondary?.question_group_secondary_id;
		return isAssignedToMe && hasQuestionary ? "questions" : "activity";
	});

	const haveSubscription = ticket_subscriptions?.find(
		(sub) => sub.email === currentUser?.email
	);

	const [questionGroup, setQuestionGroup] = useState(null);
	const [questionGroupId, setQuestionGroupId] = useState(null);
	const [questionLoading, setQuestionLoading] = useState(false);
	const [notificationQuestion, setNotificationQuestion] = useState(false);
	const [answerOpen, setAnswerOpen] = useState([]);
	const [answerClose, setAnswerClose] = useState([]);
	const [questionRequired, setQuestionRequired] = useState(null);
	const [questionGroupSecondary, setQuestionGroupSecondary] = useState(null);
	const [answerComplete, setAnswerComplete] = useState(false);
	const [loadingFavorite, setLoadingFavorite] = useState(false);
	const [loadingStatus, setLoadingStatus] = useState(false);

	// Tri-state local del cuestionario (computation mas fina que el prop del vm).
	const questionnaireState = useMemo(() => {
		const hasSecondaryGroupId =
			!!ticket_category_secondary?.question_group_secondary_id;
		const eligibleSync = canViewQuestionAndResponse && hasSecondaryGroupId;
		if (!eligibleSync) return "hidden";
		if (!questionGroup) return "loading";
		return isQuestionnaireOnlyTitle(questionGroup) ? "hidden" : "ready";
	}, [
		canViewQuestionAndResponse,
		ticket_category_secondary?.question_group_secondary_id,
		questionGroup,
	]);

	// Cuestionario "de informacion" (el que se responde al crear la tarea, en
	// `question_group_primary_id`) viaja al campo descripcion del hallazgo en
	// vez de ocupar su propia pestania. Aplica a todo primary respondido salvo
	// auditorias (audit=true), que se quedan en tab porque generan incidencias.
	// Decision Sabri 2026-06-04: "siempre informacion de la tarea deberia ir
	// en descripcion".
	const shouldInlinePrimaryAnswersInDescription =
		!!ticket_category_secondary?.question_group_primary_id &&
		!ticket_category_secondary?.audit &&
		answerOpen?.length > 0;

	// Subset del anterior: cuando ademas NO hay cuestionario secondary, el
	// hallazgo es de solo lectura (el primary respondido al crear es el estado
	// final, no hay gestion pendiente). En ese caso el CTA primario es
	// "Marcar como leido", como bots informativos viejos (Pase 19).
	const isReadOnlyInfoTicket =
		shouldInlinePrimaryAnswersInDescription &&
		!ticket_category_secondary?.question_group_secondary_id;

	// Ticket informativo D4
	const informationalIsRead =
		ticket_status?.id === CLOSED || ticket_status?.id === SYS_CLOSED;
	const informationalReadAt = useMemo(() => {
		if (!informationalIsRead) return null;
		const closedLog = ticket_log?.findLast?.(
			(l) =>
				l.ticket_status_id === CLOSED || l.ticket_status_id === SYS_CLOSED
		);
		return closedLog?.created_at || null;
	}, [informationalIsRead, ticket_log]);

	// --- Effects ---

	// El estado inicial ya cubre el montaje; esto cubre el resto: entre hallazgos
	// de la misma lista el componente se reusa en vez de remontar, y la campanita
	// puede apuntar a otro comentario sin desmontar nada.
	//
	// No depende de `selectedTab` a propósito: cambiar de pestaña a mano sigue
	// mandando hasta que se cambia de hallazgo o de comentario.
	useEffect(() => {
		if (vengoPorUnaMencion) setSelectedTab("activity");
	}, [vengoPorUnaMencion, anclaComentario, id]);

	useEffect(() => {
		const questionsTabExists =
			questionnaireState !== "hidden" ||
			(!canViewQuestionAndResponse && answerClose?.length > 0);
		if (!questionsTabExists && selectedTab === "questions") {
			setSelectedTab("activity");
		}
		if (
			isInformational &&
			(selectedTab === "questions" || selectedTab === "answers")
		) {
			setSelectedTab("activity");
		}
		if (shouldInlinePrimaryAnswersInDescription && selectedTab === "answers") {
			setSelectedTab("activity");
		}
	}, [
		questionnaireState,
		canViewQuestionAndResponse,
		answerClose?.length,
		selectedTab,
		isInformational,
		shouldInlinePrimaryAnswersInDescription,
	]);

	useEffect(() => {
		setQuestionLoading(false);
		// Al navegar a otro hallazgo (cambio de `?ticket=`) el componente se
		// reusa: React Router no desmonta, asi que el scroll queda donde
		// estaba el hallazgo anterior. Forzamos top para que el usuario
		// arranque viendo el header del nuevo hallazgo. Solo en navegacion
		// real (no en mount inicial) y solo si no es dialog (el modal tiene
		// su propio scroll container).
		if (!inDialog && typeof window !== 'undefined') {
			window.scrollTo({ top: 0, behavior: 'auto' });
		}
	}, [id, inDialog]);

	useEffect(() => {
		return () => {
			setNotificationQuestion(false);
		};
	}, [questionLoading]);

	useEffect(() => {
		const separateAnswers = (array, idOne, idTwo) => {
			let answersOpen = [];
			let answersClose = [];
			array.map((item) => {
				if (item.question_group_id === idOne) answersOpen.push(item);
				else answersClose.push(item);
			});
			setAnswerOpen(answersOpen);
			setAnswerClose(answersClose);
		};
		ticket_category_secondary &&
			separateAnswers(
				ticket_answer,
				ticket_category_secondary?.question_group_primary_id,
				ticket_category_secondary?.question_group_secondary_id
			);
	}, [ticket_category_secondary]);

	useEffect(() => {
		setQuestionGroupId(null);
	}, [id]);

	useEffect(() => {
		if (
			!ticket_category_secondary ||
			!ticket_category_secondary.question_group_secondary
		) {
			setQuestionGroup(null);
			return;
		}
		apiGetTicketsCategoriesById(ticket_category_secondary.id).then((res) => {
			const { data: resData } = res;
			if (resData?.data) {
				const group_id = resData.data.question_group_secondary_id;
				const group = resData.data.question_group_secondary[0];
				setQuestionGroupId(group_id);
				setQuestionGroupSecondary(group);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticket_category_secondary?.id]);

	useEffect(() => {
		if (questionGroupSecondary) {
			const sortedObj = JSON.parse(JSON.stringify(questionGroupSecondary));
			sortedObj.questions.sort((a, b) => a.priority - b.priority);
			setQuestionGroup(sortedObj);
			const questionIsRequired = sortedObj.questions.filter(
				(item) => item.required === true
			);
			setQuestionRequired(questionIsRequired);
			setQuestionLoading(true);
		}
	}, [questionGroupSecondary]);

	useEffect(() => {
		if (
			answerOpen &&
			!canViewQuestionAndResponse &&
			!tickets_can_view_timeline
		) {
			setSelectedTab("answers");
		}
	}, [answerOpen]);

	// --- Helpers ---
	const closeNotificationQuestions = (key) => {
		toast.remove(key);
	};

	const checkQuestionRequired = (questions) => {
		const questionIsRequired = questions.filter(
			(item) => item.required === true
		);
		setQuestionRequired(questionIsRequired);
	};

	const checkQuestionsClose = (itemCheck) => {
		setTimeout(async () => {
			if (
				operator?.id === currentUser?.id &&
				questionGroup &&
				itemCheck
			) {
				setNotificationQuestion(true);
				const notification = await toast.push(
					<Notification
						className={"w-full"}
						title={t("questions")}
						duration={5000}
					>
						<div>{t("questionnaire_notification_text")}</div>
						<div className="text-right mt-3 w-full">
							<Button
								onClick={() => {
									setSelectedTab("questions");
									closeNotificationQuestions(notification);
								}}
								size="xs"
								variant="solid"
								className="mr-2"
							>
								{t("confirm_capitalize")}
							</Button>
							<Button
								onClick={() => closeNotificationQuestions(notification)}
								size="xs"
							>
								{t("cancel_capitalize")}
							</Button>
						</div>
					</Notification>
				);
			}
		}, 100);
	};

	// setNewTicketStatus: wrapper local que gestiona loadingStatus y llama al vm callback.
	// La logica de check de preguntas se mantiene aqui porque depende del estado local
	// (questionGroup, questionRequired, answerClose).
	const setNewTicketStatus = async (status_id, ticketId, skipQuestionCheck = false) => {
		setLoadingStatus(true);
		try {
			if (
				!skipQuestionCheck &&
				operator?.id === currentUser?.id &&
				questionGroup &&
				questionRequired?.length > 0 &&
				!answerClose.length > 0 &&
				status_id === 4
			) {
				checkQuestionsClose(questionRequired.length > 0);
				return false;
			}
			const result = await onChangeStatus?.(status_id, ticketId);
			return result !== false;
		} catch (error) {
			return false;
		} finally {
			setLoadingStatus(false);
		}
	};

	// handleQuestionnaireResolution: wrapper local que llama al vm callback.
	// La logica de fallback (responsableMap, reassign_to_role) vive en el hook.
	// Para la rama 'resolve' toggleamos loadingStatus igual que setNewTicketStatus,
	// para que el boton de estado muestre el spinner mientras cierra el ticket.
	const handleQuestionnaireResolution = async (action, reassignToRole) => {
		if (!onQuestionnaireResolution) return true;
		if (action === 'resolve') {
			setLoadingStatus(true);
			try {
				return await onQuestionnaireResolution(action, reassignToRole);
			} finally {
				setLoadingStatus(false);
			}
		}
		return await onQuestionnaireResolution(action, reassignToRole);
	};

	const changeIsFavorite = async () => {
		setLoadingFavorite(true);
		try {
			await onToggleFavorite?.();
		} finally {
			setLoadingFavorite(false);
		}
	};

	const handleChangeSuscription = async () => {
		await onSetSubscriber?.();
	};

	const handleCopyClick = () => {
		navigator.clipboard.writeText(window.location.href);
		toast.push(
			<Notification title={t("copied")} type="success" duration={1000} />,
			{ placement: "top-center" }
		);
	};

	const handleAddLinkedTicket = () => {
		onAddLinkedTicket?.();
	};

	const handleLinkExisting = () => {
		onLinkExisting?.();
	};

	const handleTicketParent = () => {
		onOpenParent?.();
	};

	const handleTabChange = (val) => {
		setSelectedTab(val);
	};

	const handeDeleteTicket = () => {
		onDelete?.();
	};

	const changeOperator = () => {
		if (
			!notificationQuestion &&
			questionLoading &&
			!isQuestionnaireOnlyTitle(questionGroup) &&
			!answerClose.length > 0
		) {
			checkQuestionsClose(answerClose.length === 0);
			setTimeout(() => {
				onChangeOperator?.();
			}, 500);
		} else {
			onChangeOperator?.();
		}
	};

	// --- Status helpers (pure, no Redux) ---
	const setOptions = (option, color) => {
		if (option === CLOSED && !tickets_can_close) return {};
		if (option === REOPEN && !tickets_can_reopen) return {};
		const optionStatus = ticketStatus?.find((status) => status.id === option);
		return { ...optionStatus, color };
	};

	const filterTicketStatus = (allStatus, tStatus) => {
		if (!tStatus) return null;
		const { id: sid } = tStatus;
		switch (sid) {
			case NEW_TICKET:
				return setOptions(ASSIGNED, "bg-[#f7a511]");
			case ASSIGNED:
				return setOptions(CLOSED, "bg-red-500");
			case REOPEN:
				return setOptions(CLOSED, "bg-red-500");
			case DELETED:
				return setOptions(CLOSED, "bg-red-500");
			case CANCELLED:
				return setOptions(REOPEN, "bg-emerald-500");
			case CLOSED:
				return setOptions(REOPEN, "bg-emerald-500");
			case SYS_CLOSED:
				return setOptions(REOPEN, "bg-emerald-500");
			default:
				return null;
		}
	};

	const canChangeTicketStatus = () => {
		try {
			if (!loading) {
				const isOperator = operator?.id === currentUser?.id;
				if (ticket_status?.id === CLOSED) {
					const statusLog = ticket_log?.findLast(
						(l) => l.ticket_status_id === CLOSED
					);
					const closedAgo = timeDiff(statusLog?.created_at, "hour");
					return isOperator && closedAgo <= 48;
				}
				return isOperator;
			}
		} catch (error) {
			return false;
		}
	};

	const canChangeStatus = canChangeTicketStatus();
	const nextStatus = filterTicketStatus(ticketStatus, ticket_status);

	// --- Tabs disponibles ---
	const availableTabs = [];
	if (
		!isInformational &&
		(questionnaireState !== "hidden" ||
			(!canViewQuestionAndResponse && answerClose?.length > 0))
	) {
		availableTabs.push("questions");
	}
	if (tickets_can_view_timeline) availableTabs.push("activity");
	if (
		!isInformational &&
		!shouldInlinePrimaryAnswersInDescription &&
		ticket_answer?.length > 0 &&
		ticket_category_primary?.question_group_primary_id
	) {
		availableTabs.push("answers");
	}
	if (record) availableTabs.push("record");

	// --- Swipe entre tabs (mobile) ---
	const tabSwipeStartRef = useRef(null);
	const titleContextRef = useRef(null);

	const onTabSwipeStart = (e) => {
		const touch = e.touches[0];
		tabSwipeStartRef.current = {
			x: touch.clientX,
			y: touch.clientY,
			time: Date.now(),
		};
	};

	const onTabSwipeEnd = (e) => {
		const start = tabSwipeStartRef.current;
		tabSwipeStartRef.current = null;
		if (!start) return;
		if (availableTabs.length < 2) return;

		const touch = e.changedTouches[0];
		const dx = touch.clientX - start.x;
		const dy = touch.clientY - start.y;
		const dt = Date.now() - start.time;

		if (Math.abs(dx) < 60) return;
		if (Math.abs(dx) < Math.abs(dy) * 1.5) return;
		if (dt > 600) return;

		const currentIdx = availableTabs.indexOf(selectedTab);
		if (currentIdx === -1) return;

		if (dx < 0) {
			const nextIdx = Math.min(currentIdx + 1, availableTabs.length - 1);
			if (nextIdx !== currentIdx) setSelectedTab(availableTabs[nextIdx]);
		} else {
			const prevIdx = Math.max(currentIdx - 1, 0);
			if (prevIdx !== currentIdx) setSelectedTab(availableTabs[prevIdx]);
		}
	};

	// --- Menu de acciones del ticket ---
	const ticketActionsMenu = (
		<Dropdown
			placement="bottom-end"
			renderTitle={
				<Button
					size="sm"
					variant="plain"
					className="!p-2 !rounded-md hover:!bg-gray-100 dark:hover:!bg-gray-700"
					icon={<HiOutlineDotsVertical className="w-4 h-4" />}
				/>
			}
		>
			{tickets_can_link && (
				<>
					<Dropdown.Item eventKey="add-linked" onSelect={handleAddLinkedTicket}>
						<HiOutlinePlus className="text-base text-gray-500" />
						<span className="ml-2">{`${t("create")} ${t("ticket_alias")}`}</span>
					</Dropdown.Item>
					<Dropdown.Item eventKey="link-existing" onSelect={handleLinkExisting}>
						<HiOutlineLink className="text-base text-gray-500" />
						<span className="ml-2">{`${t("link")} ${t("ticket_alias")}`}</span>
					</Dropdown.Item>
					<Dropdown.Item variant="divider" />
				</>
			)}
			<Dropdown.Item
				eventKey="favorite"
				onSelect={() => !loadingFavorite && changeIsFavorite()}
			>
				<span className={is_favorite ? "text-amber-500" : "text-gray-500"}>
					{is_favorite ? <HiStar /> : <HiOutlineStar />}
				</span>
				<span className="ml-2">
					{is_favorite
						? t("remove_favorite", "Quitar favorito")
						: t("favorite", "Favorito")}
				</span>
			</Dropdown.Item>
			<Dropdown.Item
				eventKey="follow"
				onSelect={() => handleChangeSuscription()}
			>
				<span className={haveSubscription ? "text-red-500" : "text-gray-500"}>
					{haveSubscription ? <HiFlag /> : <HiOutlineFlag />}
				</span>
				<span className="ml-2">
					{haveSubscription ? t("unfollow") : t("follow")}
				</span>
			</Dropdown.Item>
			<Dropdown.Item eventKey="copy" onSelect={handleCopyClick}>
				<HiOutlineDuplicate className="text-base text-gray-500" />
				<span className="ml-2">{t("copy")}</span>
			</Dropdown.Item>
			{tickets_can_cancel && (
				<>
					<Dropdown.Item variant="divider" />
					<Dropdown.Item eventKey="delete" onSelect={() => handeDeleteTicket()}>
						<HiOutlineTrash className="text-base text-red-500" />
						<span className="ml-2 text-red-600 dark:text-red-400">
							{t("delete")}
						</span>
					</Dropdown.Item>
				</>
			)}
		</Dropdown>
	);

	return (
		<>
			<div
				className={
					inDialog
						? "overflow-visible"
						: pathname === "/tickets/inbox"
						? "min-h-full items"
						: "overflow-y-auto max-h-[500px]"
				}
				id="ticketContent"
			>
				{/* TicketTopbar — solo boton Inbox en mobile, y solo en el detalle a
				    pantalla completa del inbox. Ahora flota sobre el viewport en vez de
				    ocupar una fila, asi que dentro del dialogo o embebido en otra
				    pantalla quedaria suelto arriba de todo, apuntando a un lugar del
				    que el usuario no vino. */}
				{!inDialog && pathname === "/tickets/inbox" && <TicketTopbar />}

				<div
					className="px-4 sm:px-6 pt-3 mx-auto"
					style={{ maxWidth: "80rem" }}
				>
					{/* La barra de convivencia se fue de acá a `TicketInboxDetail`,
					    arriba del cambio entre esqueleto y contenido: este componente
					    se desmonta en cada carga de hallazgo y la barra parpadeaba
					    con cada clic. */}

					{/* CTA "Marcar como leido": en desktop vive en el header
					    (primaryAction de TicketTitleContext), en mobile sticky
					    al pie del contenido principal. Mismo componente, dos
					    posiciones segun viewport. */}
					<TicketTitleContext
						ref={titleContextRef}
						data={data}
						actionsSlot={ticketActionsMenu}
						primaryAction={
							!smaller.md && (isInformational || isReadOnlyInfoTicket) ? (
								<TicketPrimaryActions
									mode="header"
									isInformational={isInformational || isReadOnlyInfoTicket}
									informationalIsRead={informationalIsRead}
									informationalReadAt={informationalReadAt}
									loadingStatus={loadingStatus}
									canMarkAsRead={isAssignedToMe}
									onMarkAsRead={() => setNewTicketStatus(CLOSED, id)}
								/>
							) : null
						}
					/>

					<TicketMetaStrip
						data={data}
						onChangeOperator={changeOperator}
						canUpdate={tickets_can_update}
						loadingUsersAllowed={loadingUsersAllowed}
					/>

					<LinkTicketDialog />

					{/* Grid con columna principal + aside lateral.
					    pb-28 en mobile reserva espacio para el bar fixed bottom
					    (TicketPrimaryActions mode=mobileSticky). Se neutraliza
					    en lg+ donde el bar no se renderiza.
					    inDialog: dentro del modal el viewport sigue siendo ancho
					    pero el contenedor es angosto (~700px), asi que los
					    breakpoints lg: de viewport apretarian el aside al costado.
					    Forzamos columna unica para que el panel de Detalles baje
					    debajo del contenido, igual que en la vista mobile. El pb
					    se ata a smaller.md (cuando realmente se monta el bar
					    fixed-bottom), no a lg:, para no dejar 7rem huerfanos en
					    tablets (768-1023px) dentro del modal donde no hay bar.

					    El aside pasa al costado en `xl` (1280) y no en `2xl` (1536).
					    Con 1536 el panel de Detalles se le iba abajo a cualquiera con
					    un MacBook Air de 13", que de fábrica viene en 1470x956: 66px
					    del lado malo del breakpoint. Sabri lo reporto en la reunion
					    del 18/08 y se veia en que al 90% de zoom se le acomodaba, o
					    sea cuando el viewport pasaba de 1470 a 1633.

					    1280 y no un valor intermedio porque es el mismo breakpoint con
					    el que `TicketInboxDetail` decide mostrar el detalle al lado de
					    la lista (`hidden xl:flex`). Si a ese ancho el layout ya se
					    compromete a poner dos paneles en fila, el grid de adentro no
					    tiene por que esperar 256px mas. */}
					<div className={inDialog
						? `grid grid-cols-1 gap-6 ${smaller.md ? "pb-28" : "pb-0"}`
						: "grid grid-cols-1 xl:grid-cols-3 gap-6 pb-28 xl:pb-0"}>
						<div className={inDialog ? "min-w-0" : "xl:col-span-2 min-w-0"}>
							{description ? (
								audit_cam ? (
									<div className="mb-5">
										<div className="ql-editor ticketInbox max-w-none">
											{ReactHtmlParser(convertUrlToLink(description))}
										</div>
									</div>
								) : (
									<div className="mb-6 [&_.ql-editor]:!p-0">
										<div className="ql-editor ticketInbox max-w-none">
											{ReactHtmlParser(convertUrlToLink(description))}
										</div>
									</div>
								)
							) : (
								!(shouldInlinePrimaryAnswersInDescription && answerOpen?.length > 0) && (
									<div className="mb-6 pl-4 border-l-2 border-dashed border-gray-200 dark:border-gray-700/60">
										<p className="text-[13px] italic text-gray-400 dark:text-gray-500 leading-relaxed">
											{t("ticket_no_description", "Este hallazgo no tiene descripción.")}
										</p>
									</div>
								)
							)}

							{/* Adjuntos del hallazgo inline con la descripcion: cuando la
							    descripcion referencia "la foto adjunta", las imagenes
							    tienen que estar visibles ahi mismo, no escondidas en el
							    aside. Decision Sabri 2026-05-26: solo los inputs externos
							    al hallazgo (adjuntos al crear, cuestionario respondido
							    que origino, parent/incidencia). Los adjuntos sumados por
							    comentarios viven en la pestana Actividad y NO se duplican
							    aca (esos son evidencia de gestion, no del problema). */}
							{(() => {
								// Filtrar los adjuntos que provienen de ticket_reply
								// (comentarios). En ticket_attachment se mergean junto con
								// los del hallazgo original; los identificamos por id
								// presente en algun ticket_reply.ticket_reply_attachment.
								const replyAttIds = new Set(
									(ticket_reply || []).flatMap((r) =>
										(r.ticket_reply_attachment || []).map((a) => a.id)
									)
								);
								const hallazgoAtts = (ticket_attachment || []).filter(
									(a) => !replyAttIds.has(a.id)
								);
								if (hallazgoAtts.length === 0) return null;
								return (
									<div className="mb-6">
										<TicketAttachmentGallery attachments={hallazgoAtts} />
									</div>
								);
							})()}

							{/* Pase 19: info-notification muestra respuestas del cuestionario
							    de informacion en la descripcion. */}
							{shouldInlinePrimaryAnswersInDescription && answerOpen?.length > 0 && (
								<div className="mb-6 pl-4 border-l-2 border-indigo-200/80 dark:border-indigo-500/30">
									<TicketAnswers
										ticket={data}
										ticket_answer={answerOpen}
										question_group={
											ticket_category_primary?.question_group_primary
										}
										ticket_answer_attempt={ticket_answer_attempt}
									/>
								</div>
							)}

							<div>
								<Tabs value={selectedTab} onChange={handleTabChange}>
									{availableTabs.length > 0 && (
										<div className="flex lg:items-center justify-between flex-col lg:flex-row gap-4">
											{/* !items-end overridea items-center del .tab-list
											    para que el underline activo quede a la misma
											    altura cuando una tab hace wrap a 2 lineas
											    en mobile (ej "Tareas vinculadas"). */}
											<TabList className="!items-end">
												{/* !isInformational: una subcategoría 'notification'
												    puede conservar su cuestionario de cierre (el
												    vínculo ya no se borra al marcarla). Sin este
												    guard el tab se dibujaba pero quedaba inerte:
												    availableTabs ya lo excluye y el effect de arriba
												    devuelve el foco a "activity" al clickearlo. */}
												{!isInformational && questionnaireState !== "hidden" && (
													<TabNav variant="storehub" value="questions">
														{answerClose.length
															? t("answers_closed")
															: t("question_group_capitalize")}
													</TabNav>
												)}
												{!isInformational &&
													!canViewQuestionAndResponse &&
													answerClose?.length > 0 && (
														<TabNav value="questions">
															{t("answers_closed")}
														</TabNav>
													)}
												{tickets_can_view_timeline && (
													<TabNav
														colorStorehub="text-sky-500"
														value="activity"
													>
														{t("activity")}
													</TabNav>
												)}
												{!shouldInlinePrimaryAnswersInDescription &&
													ticket_answer?.length > 0 &&
													ticket_category_primary?.question_group_primary_id && (
														<TabNav value="answers">
															{t("answers_open")}
														</TabNav>
													)}
												{record && (
													<TabNav value="record">{t("record")}</TabNav>
												)}
												{/* La pestaña de vinculadas se fue al aside: las derivadas y la
												    principal viven juntas en TicketLinkedCard, debajo de Detalles. */}
											</TabList>
										</div>
									)}
									<div
										className="mt-4 mx-1 touch-pan-y"
										onTouchStart={onTabSwipeStart}
										onTouchEnd={onTabSwipeEnd}
									>
										<Suspense fallback={null}>
											<TabContent value="questions" className="flex flex-col">
												{answerClose && answerClose?.length > 0 ? (
													<Suspense fallback={<AnswersSkeleton />}>
														<TicketAnswers
															ticket={data}
															ticket_answer={answerClose}
															question_group={
																ticket_category_secondary?.question_group_secondary
															}
															ticket_answer_attempt={ticket_answer_attempt}
															distance={true}
														/>
													</Suspense>
												) : questionnaireState === "loading" ? (
													<QuestionnaireSkeleton />
												) : (
													canViewQuestionAndResponse &&
													questionGroup && (
														<Suspense fallback={<QuestionnaireSkeleton />}>
															<TicketQuestion
																questionGroupSecondary={questionGroup}
																questionSecondaryGroupId={questionGroupId}
																ticketId={id}
																store={store}
																setNewTicketStatus={setNewTicketStatus}
																assigned={ASSIGNED}
																id={id}
																ticketStatus={ticket_status?.id}
																setAnswerComplete={setAnswerComplete}
																answerComplete={answerComplete}
																onQuestionnaireResolution={
																	handleQuestionnaireResolution
																}
															/>
														</Suspense>
													)
												)}
											</TabContent>
											<TabContent value="activity">
												<TicketAddComment
													ticket={data}
													disabled={loadingStatus}
												/>
												{tickets_can_view_timeline && (
													<TicketActivity
														ticket_reply={ticket_reply}
														ticket_log={ticket_log}
														ticket_answer={ticket_answer_attempt}
														loadingSelectedTicket={loading}
													/>
												)}
											</TabContent>
											<TabContent value="answers">
												{!shouldInlinePrimaryAnswersInDescription &&
													ticket_answer?.length > 0 &&
													ticket_category_primary?.question_group_primary_id && (
														<Suspense fallback={<AnswersSkeleton />}>
															<TicketAnswers
																ticket={data}
																ticket_answer={answerOpen}
																question_group={
																	ticket_category_primary?.question_group_primary
																}
																ticket_answer_attempt={ticket_answer_attempt}
															/>
														</Suspense>
													)}
											</TabContent>
											<TabContent value="record">
												{record && (
													<TicketRecod
														record={{ ...record, second_start }}
													/>
												)}
											</TabContent>
										</Suspense>
									</div>
								</Tabs>
							</div>

							{smaller.md && (isInformational || isReadOnlyInfoTicket) && (
								<TicketPrimaryActions
									mode="mobileSticky"
									isInformational={isInformational || isReadOnlyInfoTicket}
									informationalIsRead={informationalIsRead}
									informationalReadAt={informationalReadAt}
									loadingStatus={loadingStatus}
									canMarkAsRead={isAssignedToMe}
									onMarkAsRead={() => setNewTicketStatus(CLOSED, id)}
								/>
							)}

							{/* Bar mobile fixed-bottom para hallazgos de tipo accion.
							    Se oculta SOLO cuando hay un cuestionario activo SIN
							    respuestas (caso "estoy llenando el cuestionario"): ahi
							    el "Responder" del footer ya cubre el CTA. Cuando el
							    cuestionario ya esta respondido (answerClose.length > 0)
							    igual mostramos Asignar/Solucionar porque el usuario
							    necesita cerrar el hallazgo. No aplica a info-notifications
							    (esas usan TicketPrimaryActions). */}
							{smaller.md
								&& !isInformational
								&& !isReadOnlyInfoTicket
								&& !(questionnaireState === "ready" && (!answerClose || answerClose.length === 0))
								&& canChangeStatus
								&& (() => {
									const ctaContext = deriveCtaContext(data, t, ticketOrigin?.key)
									// Legacy (sin type ni action ni incidence) o notification ya cubiertos
									// por otros componentes (chip estado / TicketPrimaryActions).
									if (ctaContext.isLegacy || ctaContext.isInformative) return null
									return (
										<div
											className="fixed inset-x-0 bottom-0 z-30 px-4 pt-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
											style={{ paddingBottom: 'max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
										>
											<CtaGroup
												isInformative={false}
												isLegacy={false}
												noResponsible={ctaContext.noResponsible}
												ctaSolveLabel={ctaContext.ctaSolveLabel}
												canChangeStatus={canChangeStatus}
												canUpdate={tickets_can_update}
												loading={loadingStatus}
												onAssign={changeOperator}
												onSolve={(statusId) => setNewTicketStatus(statusId || CLOSED, id)}
												t={t}
												block
											/>
										</div>
									)
								})()}
						</div>

						<aside className={inDialog ? undefined : "xl:col-span-1 xl:sticky xl:top-3 xl:self-start"}>
							<TicketDetailsCard
								data={data}
								onChangeOperator={changeOperator}
								canUpdate={tickets_can_update}
								loadingUsersAllowed={loadingUsersAllowed}
								nextStatus={nextStatus}
								canChangeStatus={canChangeStatus}
								onChangeStatus={(statusId) => setNewTicketStatus(statusId, id)}
								loadingStatus={loadingStatus}
							/>
							{/* Un solo lugar para las vinculadas, esten arriba o abajo en el
							    arbol. Antes las derivadas vivian en una pestana y la principal
							    en esta card, con el mismo nombre y distinto comportamiento. */}
							<TicketLinkedCard
								ticket={data}
								parentId={parent_id || null}
								onOpenParent={handleTicketParent}
								onOpenTicket={onOpenTicket}
								canUnlink={tickets_can_update}
							/>
							<TicketAttachmentsCard
								ticket_attachment={ticket_attachment}
							/>
						</aside>
					</div>
					<div className="md:hidden h-20" aria-hidden="true" />
				</div>
			</div>
			<TicketDeleteConfirmation />
			<TicketChangeStatusConfitmation />
			<ChangeOperatorDialog />
		</>
	);
}
